import {useState, Dispatch, SetStateAction} from 'react';
import Spinner from '../../common/Spinner';
import Row from '../alert_alarm/Row';
import {Button} from '../../common/Button';
import {
  EditableFieldProps,
  TargetTimeSettingsResponseProps
} from './types';
import {FC} from 'react';
import {formattedTime, convertTimeInSeconds} from './helpers';
import {
  updateTargetTimeSettings,
  getTargetTimeSettings,
  saveDraftTargetTimeSettings,
  revertDraftTargetTimeSettings
} from '../../../api/settings/target_task_time';
import {maxTaskTimeInSeconds} from '../alert_alarm/constants';
import TableHeaderRow from '../../common/TableHeaderRow';
import TargetProcessTime from './TargetProcessTime';
import {useTranslation} from 'react-i18next';
import {useSnackbarHook} from '../../../hooks/useSnackbar';

interface CustomConfigurationListProps {
  data: Nullable<TargetTimeSettingsResponseProps>,
  isLoading: boolean;
  processType: string;
  treatmentId: string;
  isVisibleTreatments: boolean;
  setTargetTimeSettings:
    Dispatch<TargetTimeSettingsResponseProps> | Dispatch<SetStateAction<null>>;
  setIsShowDraftVersion: Dispatch<boolean>;
}

const CustomConfigurationList: FC<CustomConfigurationListProps> = ({
  data,
  isLoading,
  processType,
  treatmentId,
  isVisibleTreatments,
  setTargetTimeSettings,
  setIsShowDraftVersion
}) => {
  const {t} = useTranslation('targetTaskTime');
  const fields = [
    {
      name: 'taskName',
      label: t('taskName'),
      permission: null
    },
    {
      name: 'taskTime',
      label: t('taskTime'),
      permission: null
    }
  ];

  const [isResponseLoading, setIsResponseLoading] = useState(false);
  const [isTaskTimeChanged, setIsTaskTimeChanged] = useState(false);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const isButtonDisabled =
  data?.draft.changed || !isResponseLoading && isTaskTimeChanged;

  const onClickButtonHandler = async (
      func: (processType: string, treatmentId: string) => Promise<void>,
      toastMessage: string
  ) => {
    setIsResponseLoading(true);

    try {
      const treatmentIdParams = isVisibleTreatments ? treatmentId : '';
      await func(processType, treatmentIdParams);
      addSuccessToast(toastMessage);
      const targetTimeSettingsResponse =
      await getTargetTimeSettings(processType, treatmentIdParams);

      setTargetTimeSettings(targetTimeSettingsResponse);
      setIsShowDraftVersion(false);
    } catch (e) {
      addErrorToast(e?.message);
    } finally {
      setIsResponseLoading(false);
      setIsTaskTimeChanged(false);
    }
  };

  const handleUpdate = async (
      value: EditableFieldProps,
      processType,
      taskTypeCode: string,
      treatmentId: string
  ) => {
    let timeInSeconds = convertTimeInSeconds(
        {minutes: value?.minutes, seconds: value?.seconds}
    );

    timeInSeconds = timeInSeconds > maxTaskTimeInSeconds ?
    maxTaskTimeInSeconds : timeInSeconds;

    const treatmentIdParams = isVisibleTreatments ? treatmentId : '';

    try {
      await updateTargetTimeSettings(
          processType, taskTypeCode, treatmentIdParams, timeInSeconds
      );
      const task = data?.taskSettings.find((task) =>
        task.taskType.code === taskTypeCode)?.taskType.description;
      addSuccessToast(t('toasts.updateTaskTimeMessage', {task}));
      const targetTimeSettingsResponse =
      await getTargetTimeSettings(processType, treatmentIdParams);

      setTargetTimeSettings(targetTimeSettingsResponse);
      setIsTaskTimeChanged(true);
      setIsShowDraftVersion(true);
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  return (
    <Spinner loadingText={t('customConfigurationLoadingText')} isLoading={isLoading}>
      <>
        <TargetProcessTime
          processType={data?.processType}
          processTargetTime={data?.processTargetTime}
          isVisibleTreatments={isVisibleTreatments}
          treatmentId={treatmentId}
          setTargetTimeSettings={setTargetTimeSettings}
          setIsTaskTimeChanged={setIsTaskTimeChanged}
          setIsShowDraftVersion={setIsShowDraftVersion}
        />
        <table className="table">
          <thead className="table-header">
            <TableHeaderRow
              tableData={fields}
              headerRowClassName="row"
            />
          </thead>
          <tbody className="table-body">
            {data && (
              data.taskSettings.map(({taskType, targetTime}, index) => {
                const {minutes, seconds} =
                formattedTime(targetTime);

                const cellItem = {
                  rowId: index + 1,
                  cellData: {minutes: minutes, seconds: seconds},
                  editHandler: async (id, value) =>
                    handleUpdate(
                        value, processType, taskType.code, treatmentId
                    ),
                  permission: null
                };

                return <Row
                  key={`task-row-${taskType.code}`}
                  processType={taskType.description}
                  cellItem={cellItem}
                  isRelative={false}
                  isTimeInput
                  firstCellClassName="cell-task"
                />;
              })
            )}
          </tbody>
        </table>
        <div className="buttons-wrapper">
          <Button
            label={t('saveButton')}
            disabled={!isButtonDisabled}
            onClick={
              () => onClickButtonHandler(saveDraftTargetTimeSettings, t('toasts.saveDraftMessage'))
            }
          />
          <Button
            label={t('revertButton')}
            variant="outlined"
            color="secondary"
            disabled={!isButtonDisabled}
            onClick={
              () => onClickButtonHandler(revertDraftTargetTimeSettings, t('toasts.revertDraftMessage'))
            }
          />
        </div>
      </>
    </Spinner>
  );
};

export default CustomConfigurationList;
